import { baseApi } from './baseApi'

import {
  BillingPaymentReceiptDeliveryMethod,
  BillingPaymentReceiptDto,
  BillingPaymentReceiptsList,
  BillingPaymentReceiptsListQuery,
} from 'store/types'

type GenerateReceiptRequest = {
  patientId: number
  paymentId?: string
  // Scope the receipt to one charge — its payments and patient credits only.
  chargeId?: string
  // A simple receipt for one patient payment: proof of payment, nothing clinical.
  prepaymentId?: string
}

type SendReceiptRequest = {
  patientId: number
  receiptId: string
  deliveryMethods: BillingPaymentReceiptDeliveryMethod[]
}

type BatchSendReceiptsRequest = {
  patientId: number
  receiptIds: string[]
  deliveryMethods: BillingPaymentReceiptDeliveryMethod[]
}

export const paymentReceiptsApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    generatePaymentReceipt: builder.mutation<BillingPaymentReceiptDto, GenerateReceiptRequest>({
      query: ({ patientId, ...body }) => ({
        url: `/lab/patients/${patientId}/account/receipts`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['PaymentReceipts'],
    }),
    getPaymentReceipts: builder.query<
      BillingPaymentReceiptsList,
      BillingPaymentReceiptsListQuery
    >({
      query: ({ patientId, ...params }) => ({
        url: `/lab/patients/${patientId}/account/receipts`,
        params,
      }),
      providesTags: ['PaymentReceipts'],
    }),
    sendPaymentReceipt: builder.mutation<BillingPaymentReceiptDto, SendReceiptRequest>({
      query: ({ patientId, receiptId, ...body }) => ({
        url: `/lab/patients/${patientId}/account/receipts/${receiptId}/send`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['PaymentReceipts'],
    }),
    batchSendPaymentReceipts: builder.mutation<void, BatchSendReceiptsRequest>({
      query: ({ patientId, ...body }) => ({
        url: `/lab/patients/${patientId}/account/receipts/batch/send`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['PaymentReceipts'],
    }),
    getPaymentReceiptDownload: builder.query<
      { url: string },
      { patientId: number; receiptId: string; download?: boolean }
    >({
      // `download` asks S3 for an attachment disposition. The lab listings omit
      // it and keep opening the PDF in a tab.
      query: ({ patientId, receiptId, download }) => ({
        url: `/lab/patients/${patientId}/account/receipts/${receiptId}/download`,
        params: download ? { download: true } : undefined,
      }),
    }),
  }),
})

export const {
  useGeneratePaymentReceiptMutation,
  useGetPaymentReceiptsQuery,
  useSendPaymentReceiptMutation,
  useBatchSendPaymentReceiptsMutation,
  useLazyGetPaymentReceiptDownloadQuery,
} = paymentReceiptsApi
